import { Loader2, Mic, RotateCcw, Square, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { toast } from "@/shared/hooks/use-toast";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Checkbox } from "@/shared/ui/checkbox";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { pauseOtherAudio } from "@/shared/lib/exclusiveAudio";
import { digitalHumanService, type VoiceProfileDto } from "../../api/digitalHumanService";
import {
  DH_VOICE_CONSENT_TEXT,
  DH_VOICE_CONSENT_TEXT_VERSION,
  DH_VOICE_RECORD_MAX_SECONDS,
  DH_VOICE_RECORD_MIN_SECONDS,
  DH_VOICE_SAMPLE_RULE,
} from "../../model/dhConsentText";
import { errorText } from "@/shared/lib/apiErrors";

/**
 * 声纹「即录即证」卡(B5 二刀,终稿屏⑦):现场朗读授权文本 → 浏览器录音(10~19s,超上游 20s
 * 硬限自动停)→ 客户端转 WAV → 打声纹克隆端点(source=record + consentTextVersion,录音即存证)。
 * 失败/中断无半态:未提交前一切都在本地,提交失败整请求 4xx/5xx(后端已保证无孤儿档案)。
 *
 * ★0901 加「上传声音」旁路(老板 IP 流反馈:当场录有杂音,录音棚干声只能靠传):
 * - 入口在 supported 判断**之外** —— 不支持录音的浏览器上它是唯一出路,旧文案指路
 *   「改用上传样本」但这卡里根本没有那个入口(死胡同,与 0812 走错门 toast 同型)。
 * - 授权姿势跟声音库页:upload 拿不到现场朗读存证,改勾选确认;record 路即录即证不变。
 * - 文件**原样上送**(mp3/wav/m4a 后端本就收):blobToWav 是给浏览器 webm 录音的转码,
 *   对用户文件重编码只会白降一次质量。
 */

/** 录音 blob(webm/ogg)→ 16-bit PCM 单声道 WAV(后端只收 mp3/wav/m4a;浏览器原生只出 webm)。 */
async function blobToWav(blob: Blob): Promise<File> {
  const ctx = new AudioContext();
  try {
    const decoded = await ctx.decodeAudioData(await blob.arrayBuffer());
    const ch0 = decoded.getChannelData(0);
    // 双声道下混单声道(声纹样本无需立体声,体积减半)。
    const mono = new Float32Array(decoded.length);
    if (decoded.numberOfChannels > 1) {
      const ch1 = decoded.getChannelData(1);
      for (let i = 0; i < decoded.length; i++) mono[i] = (ch0[i] + ch1[i]) / 2;
    } else {
      mono.set(ch0);
    }
    const rate = decoded.sampleRate;
    const buf = new ArrayBuffer(44 + mono.length * 2);
    const v = new DataView(buf);
    const wstr = (off: number, s: string) => {
      for (let i = 0; i < s.length; i++) v.setUint8(off + i, s.charCodeAt(i));
    };
    wstr(0, "RIFF");
    v.setUint32(4, 36 + mono.length * 2, true);
    wstr(8, "WAVE");
    wstr(12, "fmt ");
    v.setUint32(16, 16, true);
    v.setUint16(20, 1, true); // PCM
    v.setUint16(22, 1, true); // mono
    v.setUint32(24, rate, true);
    v.setUint32(28, rate * 2, true);
    v.setUint16(32, 2, true);
    v.setUint16(34, 16, true);
    wstr(36, "data");
    v.setUint32(40, mono.length * 2, true);
    for (let i = 0; i < mono.length; i++) {
      const s = Math.max(-1, Math.min(1, mono[i]));
      v.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return new File([buf], "voice-consent-record.wav", { type: "audio/wav" });
  } finally {
    void ctx.close();
  }
}

export function VoiceRecordEnrollCard({
  disabled = false,
  onEnrolled,
}: {
  /** 配额已满等外部禁用态(卡仍渲染,按钮禁 + 原因由父层提示)。 */
  disabled?: boolean;
  /** 克隆成功回调(口播工作台用:建成即选中当前音色)。 */
  onEnrolled?: (p: VoiceProfileDto) => void;
}) {
  const qc = useQueryClient();
  const supported =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== "undefined";

  const [phase, setPhase] = useState<"idle" | "recording" | "review" | "submitting">("idle");
  const [seconds, setSeconds] = useState(0);
  const [label, setLabel] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // 0901 上传旁路:有值 = 本次样本来自上传(submit 走 source=upload + 勾选授权)。
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadConsent, setUploadConsent] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const blobRef = useRef<Blob | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const secondsRef = useRef(0);

  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  // 卸载兜底:停录音轨 + 回收 blob 预览 URL。
  useEffect(() => {
    return () => {
      stopTimer();
      recorderRef.current?.stream.getTracks().forEach((t) => t.stop());
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const startRecording = async () => {
    setError(null);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError("无法访问麦克风,请在浏览器地址栏允许麦克风权限后重试。");
      return;
    }
    const rec = new MediaRecorder(stream);
    recorderRef.current = rec;
    chunksRef.current = [];
    rec.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    rec.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      stopTimer();
      const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
      blobRef.current = blob;
      if (secondsRef.current < DH_VOICE_RECORD_MIN_SECONDS) {
        setError(`录音太短(不足 ${DH_VOICE_RECORD_MIN_SECONDS} 秒),请完整朗读引导文本后再停止。`);
        setPhase("idle");
        return;
      }
      setPreviewUrl((old) => {
        if (old) URL.revokeObjectURL(old);
        return URL.createObjectURL(blob);
      });
      setPhase("review");
    };
    secondsRef.current = 0;
    setSeconds(0);
    setPhase("recording");
    rec.start();
    timerRef.current = setInterval(() => {
      secondsRef.current += 1;
      setSeconds(secondsRef.current);
      // 上游声纹样本 >20s 直接拒 → 到线自动停,别让用户白录。
      if (secondsRef.current >= DH_VOICE_RECORD_MAX_SECONDS) {
        recorderRef.current?.stop();
      }
    }, 1000);
  };

  const stopRecording = () => recorderRef.current?.stop();

  const reset = () => {
    blobRef.current = null;
    setUploadedFile(null);
    setUploadConsent(false);
    setPreviewUrl((old) => {
      if (old) URL.revokeObjectURL(old);
      return null;
    });
    setError(null);
    setPhase("idle");
  };

  // 与声音库页同一收口正则(mp3/wav/m4a)—— 后端只收这三种,类型在入口处就说清。
  const AUDIO_TYPES = /^audio\/(mpeg|mp3|wav|x-wav|wave|mp4|m4a|x-m4a)$/;
  const onPickUpload = (f: File | undefined) => {
    if (!f) return;
    if (!AUDIO_TYPES.test(f.type)) {
      setError("声音样本仅支持 mp3 / wav / m4a 音频。");
      return;
    }
    // 覆盖任何在途录音成果:一次只克隆一个样本,后选的赢。
    blobRef.current = null;
    setUploadedFile(f);
    setUploadConsent(false);
    setError(null);
    setPreviewUrl((old) => {
      if (old) URL.revokeObjectURL(old);
      return URL.createObjectURL(f);
    });
    setPhase("review");
  };

  const submit = async () => {
    if (uploadedFile) {
      if (!uploadConsent) return;
      setPhase("submitting");
      setError(null);
      try {
        const p = await digitalHumanService.enrollVoiceProfile(uploadedFile, label.trim(), true, {
          source: "upload",
        });
        await qc.invalidateQueries({ queryKey: ["dh", "voice-profiles"] });
        toast({ title: "已克隆你的声音", description: "上传的样本已复刻为音色,出片可选。" });
        reset();
        setLabel("");
        onEnrolled?.(p);
      } catch (e) {
        setError(errorText(e));
        setPhase("review");
      }
      return;
    }
    const blob = blobRef.current;
    if (!blob) return;
    setPhase("submitting");
    setError(null);
    try {
      const file = await blobToWav(blob);
      const p = await digitalHumanService.enrollVoiceProfile(file, label.trim(), true, {
        source: "record",
        consentTextVersion: DH_VOICE_CONSENT_TEXT_VERSION,
      });
      await qc.invalidateQueries({ queryKey: ["dh", "voice-profiles"] });
      toast({ title: "已克隆你的声音", description: "朗读录音已作为授权存证一并归档。" });
      reset();
      setLabel("");
      onEnrolled?.(p);
    } catch (e) {
      setError(errorText(e));
      setPhase("review"); // 留在试听态,可直接重试提交或重录
    }
  };

  // 上传入口(0901):idle 与「不支持录音」两个分支共用;选完即进 review 试听。
  const uploadInput = (
    <>
      <input
        id="dh-voice-upload"
        data-testid="dh-voice-upload-input"
        type="file"
        accept=".mp3,.wav,.m4a,audio/mpeg,audio/wav,audio/x-m4a,audio/mp4"
        className="hidden"
        onChange={(e) => {
          onPickUpload(e.target.files?.[0]);
          e.target.value = ""; // 同名文件重选也要触发 change
        }}
      />
      <Label
        htmlFor="dh-voice-upload"
        className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-normal hover:bg-muted"
      >
        <Upload className="h-3.5 w-3.5" />
        上传现成录音
      </Label>
    </>
  );

  return (
    <Card className="border-dashed shadow-none" data-testid="dh-voice-record-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Mic className="h-4 w-4 text-primary" />
          用我自己的声音
          <span className="text-xs font-normal text-muted-foreground">
            朗读 {DH_VOICE_RECORD_MIN_SECONDS} 秒以上,录到 {DH_VOICE_RECORD_MAX_SECONDS}{" "}
            秒自动停,即录即证; 或上传一段现成干声(嫌现场杂音就用这条)
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {phase === "review" || phase === "submitting" ? (
          // ── 试听 / 提交(录音与上传共用;上传路多一道授权勾选) ──
          <div className="space-y-3">
            {previewUrl && (
              <audio
                onPlay={pauseOtherAudio}
                controls
                src={previewUrl}
                className="h-8 w-full max-w-xs"
                aria-label="试听录音"
              >
                {/* 录音无字幕轨(a11y 规则占位)。 */}
                <track kind="captions" />
              </audio>
            )}
            {uploadedFile && (
              <>
                <p className="text-xs text-muted-foreground">
                  已选「{uploadedFile.name}」· {DH_VOICE_RECORD_MIN_SECONDS}~
                  {DH_VOICE_RECORD_MAX_SECONDS} 秒安静干声效果最好, 过长会被上游拒收。
                </p>
                {/* ★样本内容红线(0915 归一批 A 从声音库右卡搬来 —— 右卡整张已退役,但这条
                    不能跟着删):它是 0813 实测教训 —— 用户传了「数 1 到 10」,出片就把数数
                    夹进了口播。录音路有引导文本兜着,上传路此前一句引导都没有。 */}
                <p
                  data-testid="dh-upload-sample-rule"
                  className="rounded-md bg-warning/10 px-2.5 py-2 text-xs text-muted-foreground"
                >
                  <b className="font-medium text-foreground">样本里说什么很重要:</b>
                  {DH_VOICE_SAMPLE_RULE}
                </p>
              </>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="dh-record-label" className="text-xs">
                音色名称(可选)
              </Label>
              <Input
                id="dh-record-label"
                value={label}
                maxLength={30}
                placeholder="例如:我的播报声"
                onChange={(e) => setLabel(e.target.value)}
                className="max-w-xs text-sm"
              />
            </div>
            {uploadedFile && (
              // 上传路拿不到现场朗读存证 → 授权改勾选(与声音库页同款文案)。
              <label htmlFor="dh-voice-upload-consent" className="flex items-start gap-2 text-sm">
                <Checkbox
                  id="dh-voice-upload-consent"
                  checked={uploadConsent}
                  onCheckedChange={(v) => setUploadConsent(v === true)}
                  aria-label="声纹授权确认"
                />
                <span>我确认该语音为本人所有 / 已获授权,同意复刻为音色用于出片。</span>
              </label>
            )}
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                disabled={phase === "submitting" || disabled || (!!uploadedFile && !uploadConsent)}
                onClick={submit}
              >
                {phase === "submitting" && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                克隆这段声音
              </Button>
              <Button variant="outline" size="sm" disabled={phase === "submitting"} onClick={reset}>
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                {uploadedFile ? "重选" : "重录"}
              </Button>
              {/* 计费透出(2026-09-08 声音 V2 收尾):扣多少在扣之前说,不让用户提交完看账单才知道。 */}
              <span className="text-2xs text-muted-foreground" data-testid="dh-enroll-cost">
                克隆消耗 10 积分
              </span>
            </div>
          </div>
        ) : !supported ? (
          // ── 浏览器录不了音:上传是唯一出路,必须给真入口(不能只指路) ──
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              当前浏览器不支持录音 —— 直接上传一段现成录音(mp3 / wav / m4a),或换用最新版 Chrome/Edge
              现场录。
            </p>
            <div>{uploadInput}</div>
          </div>
        ) : (
          <>
            <div className="rounded-md border bg-muted/40 p-3">
              <p className="text-2xs font-medium text-muted-foreground">
                请朗读以下引导文本(版本 {DH_VOICE_CONSENT_TEXT_VERSION};录音即存证):
              </p>
              <p className="mt-1 text-sm leading-relaxed">{DH_VOICE_CONSENT_TEXT}</p>
              {/* 录音要求 chips(2026-09-08 声音 V2):环境与念法直接决定克隆质量
                  (样本里有什么,成片就可能夹进什么 —— 见 DH_VOICE_SAMPLE_RULE 的实测)。 */}
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="dh-record-tips">
                {["保持安静", "只有本人说话", "正常语速", "不要播音腔", "没有背景音乐"].map(
                  (tip) => (
                    <span
                      key={tip}
                      className="rounded-full border border-border/60 px-2 py-0.5 text-2xs text-muted-foreground"
                    >
                      {tip}
                    </span>
                  )
                )}
              </div>
            </div>
            {phase === "recording" ? (
              <div className="flex items-center gap-3">
                <Button variant="destructive" size="sm" onClick={stopRecording}>
                  <Square className="mr-1.5 h-3.5 w-3.5" />
                  停止录音
                </Button>
                <span className="text-sm tabular-nums text-muted-foreground">
                  <span className="mr-1.5 inline-block h-2 w-2 animate-pulse rounded-full bg-destructive" />
                  {seconds}s / {DH_VOICE_RECORD_MAX_SECONDS}s
                </span>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" disabled={disabled} onClick={startRecording}>
                  <Mic className="mr-1.5 h-3.5 w-3.5" />
                  开始录音
                </Button>
                <span className="text-xs text-muted-foreground">或</span>
                {uploadInput}
              </div>
            )}
          </>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
        {/* 卖点行(声音 V2):克隆的声线走新模型 —— 方言/多语言是「我的声纹」独有的能力,
            在人决定要不要录的这一刻说清(预置音色没有这一条)。 */}
        <p className="text-2xs text-muted-foreground" data-testid="dh-enroll-selling-point">
          克隆一次,这个声音就能说普通话、20 种方言和 16 种语言
        </p>
      </CardContent>
    </Card>
  );
}
